import { useState, useRef, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { Plus, Trash, PencilSimple, WarningCircle, Lock } from '@phosphor-icons/react';
import { formatHandicapIndex, parseHandicapInput, formatHandicapForInput, formatPlayingHandicap } from '../golf';
import { patchRound } from '../lib/api';
import { roundHasScores } from '../lib/formatHelpers';
import { setFormDirty } from '../lib/updateGuard';
import { toast } from 'sonner';
import { groupDisplayName } from '../lib/groupOrder';

function genId() { return 'p-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7); }

/**
 * @param {Object} props
 * @param {Array} props.players - Current trip players list
 * @param {Function} props.onSavePlayers - Called with updated players array (trip-level save)
 * @param {boolean} props.saving
 * @param {boolean} [props.autoFocus]
 * @param {boolean} [props.showList]
 * @param {Object|null} [props.initialEditPlayer]
 * @param {Function} [props.onActionComplete]
 * @param {Object|null} [props.roundInFocus] - The round currently selected (has .id, .teams, .group_names, .team_size)
 * @param {string} [props.tripCode] - Trip code for round PATCH
 * @param {Function} [props.onRoundUpdated] - Called with updated trip data after round PATCH
 */
export default function AddPlayerForm({ players, onSavePlayers, saving, autoFocus = true, showList = true, initialEditPlayer = null, onActionComplete, roundInFocus = null, tripCode = '', onRoundUpdated, isOrganiser = true }) {
  const [name, setName] = useState('');
  const [val, setVal] = useState('');
  const [mode, setMode] = useState('whs');
  const [editPlayer, setEditPlayer] = useState(null);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [groupSelection, setGroupSelection] = useState('none'); // 'none' | 'new' | index string '0','1',...
  const [newGroupName, setNewGroupName] = useState('');
  const [assigningSaving, setAssigningSaving] = useState(false);

  // Update guard: mark player form as dirty when name has input
  useEffect(() => {
    const dirty = name.trim().length > 0;
    setFormDirty('addPlayer', dirty);
    return () => setFormDirty('addPlayer', false);
  }, [name]);

  const nameRef = useRef(null);

  useEffect(() => {
    if (autoFocus) nameRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (initialEditPlayer) {
      setEditPlayer(initialEditPlayer);
      setName(initialEditPlayer.name);
      const m = initialEditPlayer.handicap_mode || 'whs';
      setMode(m);
      if (m === 'ph' && initialEditPlayer.manual_ph != null) {
        setVal(initialEditPlayer.manual_ph < 0 ? `+${Math.abs(initialEditPlayer.manual_ph)}` : String(initialEditPlayer.manual_ph));
      } else {
        setVal(formatHandicapForInput(initialEditPlayer.whs_index));
      }
    }
  }, [initialEditPlayer]);



  const buildPlayer = () => {
    if (mode === 'ph') {
      const parsed = parseHandicapInput(val);
      const intPH = Math.round(parsed);
      return { handicap_mode: 'ph', manual_ph: intPH, whs_index: 0 };
    }
    return { handicap_mode: 'whs', manual_ph: null, whs_index: parseHandicapInput(val) };
  };

  const handleAdd = async () => {
    if (!name.trim()) return;
    const player = { id: genId(), name: name.trim(), ...buildPlayer() };

    onSavePlayers([...players, player]);

    const wantsAssignment = roundInFocus && tripCode && groupSelection !== 'none';
    if (wantsAssignment) {
      setAssigningSaving(true);
      try {
        const teams = (roundInFocus.teams || []).map(t => [...t]);
        const groupNames = [...(roundInFocus.group_names || [])];
        const patchBody = {};

        if (groupSelection === 'new') {
          const trimmedName = newGroupName.trim();
          if (!trimmedName) { toast.error('Group name cannot be empty'); setAssigningSaving(false); return; }
          teams.push([player.id]);
          groupNames.push(trimmedName);
          patchBody.teams = teams;
          patchBody.group_names = groupNames;
        } else {
          const idx = parseInt(groupSelection, 10);
          if (idx >= 0 && idx < teams.length) {
            teams[idx] = [...teams[idx], player.id];
          } else {
            while (teams.length <= idx) teams.push([]);
            teams[idx].push(player.id);
          }
          patchBody.teams = teams;
        }

        const res = await patchRound(tripCode, roundInFocus.id, patchBody);
        if (onRoundUpdated) onRoundUpdated(res.data);
        const label = groupSelection === 'new' ? newGroupName.trim() : groupDisplayName(roundInFocus, parseInt(groupSelection, 10));
        toast.success(`${player.name} added to ${label}`);
      } catch {
        toast.error('Player added but group assignment failed — assign via Team Draw.');
      } finally { setAssigningSaving(false); }
    }

    setName(''); setVal(''); setKeypadOpen(false); setGroupSelection('none'); setNewGroupName('');
    onActionComplete?.();
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const handleEdit = () => {
    if (!editPlayer || !name.trim()) return;
    doEditSave();
  };

  const doEditSave = () => {
    const fields = buildPlayer();
    const playerChanged = name.trim() !== editPlayer.name ||
      fields.whs_index !== editPlayer.whs_index ||
      fields.manual_ph !== editPlayer.manual_ph ||
      fields.handicap_mode !== editPlayer.handicap_mode;

    if (!playerChanged) {
      onActionComplete?.();
      return;
    }

    const updated = players.map(p =>
      p.id === editPlayer.id ? { ...p, name: name.trim(), ...fields } : p
    );
    onSavePlayers(updated);
    toast.success('Player updated');

    setEditPlayer(null); setName(''); setVal(''); setKeypadOpen(false);
    onActionComplete?.();
  };

  const handleRemove = (playerId) => {
    onSavePlayers(players.filter(p => p.id !== playerId));
  };

  const openEdit = (p) => {
    setEditPlayer(p);
    setName(p.name);
    const m = p.handicap_mode || 'whs';
    setMode(m);
    if (m === 'ph' && p.manual_ph != null) {
      setVal(p.manual_ph < 0 ? `+${Math.abs(p.manual_ph)}` : String(p.manual_ph));
    } else {
      setVal(formatHandicapForInput(p.whs_index));
    }
    setKeypadOpen(false);
  };

  const cancelEdit = () => { setEditPlayer(null); setName(''); setVal(''); setKeypadOpen(false); setMode('whs'); setGroupSelection('none'); setNewGroupName(''); };

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setVal('');
    setKeypadOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && name.trim()) {
      setKeypadOpen(false);
      editPlayer ? handleEdit() : handleAdd();
    }
  };

  const handleKey = (key) => {
    if (key === 'done') { setKeypadOpen(false); return; }
    if (key === 'del') { setVal(prev => prev.slice(0, -1)); return; }
    if (key === 'clear') { setVal(''); return; }
    if (key === '+') {
      setVal(prev => {
        if (prev.startsWith('+')) return prev;
        if (prev.startsWith('-')) return '+' + prev.slice(1);
        return '+' + prev;
      });
      return;
    }
    if (key === '.') {
      if (mode === 'ph') return;
      setVal(prev => prev.includes('.') ? prev : prev + '.');
      return;
    }
    setVal(prev => prev + key);
  };

  const sortVal = (p) => {
    if (p.handicap_mode === 'ph' && p.manual_ph != null) return p.manual_ph;
    return p.whs_index;
  };

  const keypadKeys = mode === 'ph'
    ? ['1','2','3','del','4','5','6','','7','8','9','+']
    : ['1','2','3','del','4','5','6','.','7','8','9','+'];

  // Group picker helpers
  const showGroupPicker = !!roundInFocus && !editPlayer;
  const isLocked = !!editPlayer && !!roundInFocus && roundHasScores(roundInFocus);
  const teams = roundInFocus?.teams || [];
  const groupNames = roundInFocus?.group_names || [];
  const teamSize = roundInFocus?.team_size || 4;
  const currentGroupIdx = editPlayer && roundInFocus ? (roundInFocus.teams || []).findIndex(t => t.includes(editPlayer.id)) : -1;
  const currentGroupName = currentGroupIdx >= 0 ? groupDisplayName(roundInFocus, currentGroupIdx) : null;

  // Over-capacity warning (exclude current player from count if they're already in the selected group)
  const selectedGroupIdx = groupSelection !== 'none' && groupSelection !== 'new' ? parseInt(groupSelection, 10) : -1;
  const selectedGroupRawCount = selectedGroupIdx >= 0 && selectedGroupIdx < teams.length ? teams[selectedGroupIdx].length : 0;
  const selectedGroupCount = selectedGroupRawCount - (editPlayer && selectedGroupIdx >= 0 && selectedGroupIdx < teams.length && teams[selectedGroupIdx].includes(editPlayer.id) ? 1 : 0);
  const overCapacity = selectedGroupIdx >= 0 && selectedGroupCount >= teamSize;

  const isSaving = saving || assigningSaving;

  return (
    <>
    <div className="space-y-4" data-testid="add-player-form">
      <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-3">
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">Name</label>
          <Input ref={nameRef} data-testid="player-name-input" placeholder="Player name" value={name}
            onChange={(e) => setName(e.target.value)} onKeyDown={handleKeyDown}
            className="h-12 rounded-xl text-base" />
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-lg border border-[#E1E6E4] overflow-hidden" data-testid="handicap-mode-toggle">
          <button type="button" onClick={() => handleModeSwitch('whs')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${mode === 'whs' ? 'bg-[#1B4332] text-white' : 'bg-white text-[#455561] hover:bg-[#F4F6F5]'}`}
            data-testid="mode-whs-btn">WHS (Index)</button>
          <button type="button" onClick={() => handleModeSwitch('ph')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${mode === 'ph' ? 'bg-[#1B4332] text-white' : 'bg-white text-[#455561] hover:bg-[#F4F6F5]'}`}
            data-testid="mode-ph-btn">PH (Direct)</button>
        </div>

        {/* Value input */}
        <div>
          <label className="text-sm font-semibold text-[#455561] mb-1.5 block">
            {mode === 'whs' ? 'WHS Handicap Index' : 'Playing Handicap'}
          </label>
          <Input data-testid="player-whs-input" type="text" inputMode="none" readOnly
            placeholder={mode === 'whs' ? 'e.g. 18.5 or +2.0' : 'e.g. 12 or +2'}
            value={val}
            onFocus={() => setKeypadOpen(true)}
            onKeyDown={handleKeyDown} className="h-12 rounded-xl text-base cursor-pointer" />
          {mode === 'whs' && <p className="text-xs text-[#B8C7C0] mt-1">Range: +10.0 to 54.0</p>}
          {mode === 'ph' && <p className="text-xs text-[#B8C7C0] mt-1">Enter the playing handicap directly (integer). Use + for plus handicaps.</p>}
          {keypadOpen && (
            <div className="grid grid-cols-4 gap-1.5 mt-2" data-testid="whs-keypad">
              {keypadKeys.map((k, i) => k === '' ? (
                <div key={i} className="h-11" />
              ) : (
                <button key={k} type="button" onClick={() => handleKey(k)}
                  className={`h-11 rounded-lg font-semibold text-lg active:bg-[#D6DBD9] transition-colors ${
                    k === '+' ? 'bg-[#E8E5DB] text-[#1B4332]' : 'bg-[#F4F6F5] text-[#1D242B]'
                  }`}
                  data-testid={`whs-key-${k}`}>
                  {k === 'del' ? '⌫' : k}
                </button>
              ))}
              <button type="button" onClick={() => handleKey('clear')}
                className="h-11 rounded-lg bg-[#F4F6F5] text-[#455561] font-semibold text-sm active:bg-[#D6DBD9]"
                data-testid="whs-key-clear">C</button>
              <button type="button" onClick={() => handleKey('0')}
                className="h-11 rounded-lg bg-[#F4F6F5] text-[#1D242B] font-semibold text-lg active:bg-[#D6DBD9]"
                data-testid="whs-key-0">0</button>
              <button type="button" onClick={() => setKeypadOpen(false)}
                className="col-span-2 h-11 rounded-lg bg-[#1B4332] text-white font-semibold text-sm active:bg-[#163B2B]"
                data-testid="whs-key-done">Done</button>
            </div>
          )}
        </div>

        {/* Team/group picker — add mode or edit mode (organiser only) */}
        {showGroupPicker && (
          <div data-testid="group-picker-field">
            <label className="text-sm font-semibold text-[#455561] mb-1 block">
              Team / group {!editPlayer && <span className="font-normal text-[#B8C7C0]">(optional)</span>}
            </label>
            {editPlayer && (
              <p className="text-xs text-[#B8C7C0] mb-1.5" data-testid="current-group-caption">
                {currentGroupName ? `Currently in: ${currentGroupName}` : 'Currently: None (unassigned)'}
              </p>
            )}
            {isLocked ? (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#E1E6E4] bg-[#F9F9F9]" data-testid="group-picker-locked">
                <Lock className="w-4 h-4 text-[#B8C7C0]" weight="bold" />
                <span className="text-sm text-[#B8C7C0]">{currentGroupName || 'None (unassigned)'}</span>
              </div>
            ) : (
            <Select value={groupSelection} onValueChange={(v) => { setGroupSelection(v); if (v !== 'new') setNewGroupName(''); }} disabled={isLocked}>
              <SelectTrigger className="h-12 rounded-xl text-base" data-testid="group-select-trigger">
                <SelectValue placeholder="None (unassigned)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (unassigned)</SelectItem>
                {teams.map((team, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {groupDisplayName(roundInFocus, i)} ({team.length} player{team.length !== 1 ? 's' : ''})
                  </SelectItem>
                ))}
                <SelectItem value="new">
                  <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" weight="bold" /> New group…</span>
                </SelectItem>
              </SelectContent>
            </Select>
            )}
            {isLocked && <p className="text-xs text-[#B8C7C0] mt-1" data-testid="lock-caption">Locked — moves not allowed once scoring has started.</p>}
            {!isLocked && groupSelection === 'new' && (
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Group name (e.g. 9, 3A)"
                className="h-10 rounded-lg text-sm mt-2"
                maxLength={30}
                autoFocus
                data-testid="new-group-name-input"
              />
            )}
            {!isLocked && overCapacity && (
              <div className="flex items-start gap-1.5 mt-1.5" data-testid="over-capacity-warning">
                <WarningCircle className="w-4 h-4 text-[#E59000] flex-shrink-0 mt-0.5" weight="fill" />
                <p className="text-xs text-[#E59000]">Group already has {selectedGroupCount} players (team size {teamSize}). This will make it uneven.</p>
              </div>
            )}
          </div>
        )}

        {editPlayer ? (
          <div className="flex gap-2">
            <Button onClick={handleEdit} disabled={isSaving || !name.trim()}
              className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white h-12 rounded-xl font-semibold" data-testid="save-player-btn">
              {isSaving ? 'Saving...' : 'Update Player'}
            </Button>
            <Button onClick={cancelEdit} variant="outline" className="h-12 rounded-xl border-[#E1E6E4]" data-testid="cancel-edit-btn">Cancel</Button>
          </div>
        ) : (
          <Button onClick={handleAdd} disabled={isSaving || !name.trim() || (groupSelection === 'new' && !newGroupName.trim())}
            className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white h-12 rounded-xl font-semibold" data-testid="save-player-btn">
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> {isSaving ? 'Saving...' : 'Add Player'}
          </Button>
        )}
      </Card>

      {/* Player list */}
      {showList && players.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#455561] uppercase tracking-wide">{players.length} player{players.length !== 1 ? 's' : ''} added</p>
          {[...players].sort((a, b) => sortVal(a) - sortVal(b)).map(p => (
            <Card key={p.id} className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid={`player-row-${p.id}`}>
              <div className="flex items-center min-h-[52px] px-4">
                <div className="w-8 h-8 rounded-full bg-[#E8E5DB] flex items-center justify-center text-[#1B4332] font-bold text-xs mr-3 flex-shrink-0">
                  {p.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <span className="flex-1 text-sm font-medium text-[#1D242B] truncate">{p.name}</span>
                {p.handicap_mode === 'ph' ? (
                  <span className="text-sm font-bold text-[#1B4332] mr-2 tabular-nums">
                    PH {formatPlayingHandicap(p.manual_ph)} <span className="text-[9px] text-[#455561] font-normal">(manual)</span>
                  </span>
                ) : (
                  <span className="text-sm font-bold text-[#1B4332] mr-2 tabular-nums">{formatHandicapIndex(p.whs_index)}</span>
                )}
                <button onClick={() => openEdit(p)} className="p-1.5 text-[#B8C7C0] hover:text-[#1B4332] transition-colors" data-testid={`edit-player-${p.id}`}>
                  <PencilSimple className="w-4 h-4" weight="bold" />
                </button>
                <button onClick={() => handleRemove(p.id)} className="p-1.5 text-[#B8C7C0] hover:text-[#D32F2F] transition-colors" data-testid={`remove-player-${p.id}`}>
                  <Trash className="w-4 h-4" weight="bold" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>


  </>
  );
}
